import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import { useNavigate } from "react-router-dom";

function ProjectCard({ project }) {
  const navigate = useNavigate();
  const [shortDescription, setShortDescription] = useState("");

  useEffect(() => {
    async function fetchDescription() {
      try {
        const projectRef = doc(db, "projects", project.id);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const data = projectSnap.data();
          const description = data.project_description || "";
          setShortDescription(
            description.length > 100
              ? `${description.substring(0, 100)}...`
              : description
          );
        }
      } catch (error) {
        console.error("Açıklama alınırken hata:", error);
      }
    }
    fetchDescription();
  }, [project.id]);

  const firstImage =
    project.image_urls && project.image_urls.length > 0
      ? project.image_urls[0]
      : "https://via.placeholder.com/300x180?text=No+Image";

  return (
    <div style={styles.card} onClick={() => navigate(`/project/${project.id}`)}>
      <img src={firstImage} alt={project.project_name || "Proje"} style={styles.image} />
      <div style={styles.content}>
        <h3 style={styles.title}>{project.project_name || "Proje Adı"}</h3>
        
        {/* Konum bilgisi - üstte */}
        <div style={styles.locationContainer}>
          <svg style={styles.icon} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555" />
          </svg>
          <p style={styles.location}>{project.city || "Şehir Belirtilmemiş"}</p>
        </div>

        {/* Fiyat bilgisi - ayrı satırda */}
        <div style={styles.priceContainer}>
          <p style={styles.price}>
            {project.price ? `${formatPrice(project.price)}` : "Fiyat Belirtilmemiş"}
          </p>
        </div>

        {shortDescription && <p style={styles.description}>{shortDescription}</p>}
      </div>
    </div>
  );
}

// Fiyat formatlama fonksiyonu
function formatPrice(price) {
  if (!price) return "";
  
  // Eğer price string ise ve TL içeriyorsa
  if (typeof price === 'string') {
    if (price.includes('TL') || price.includes('₺')) {
      return price;
    }
    // Sadece sayısal değer varsa TL ekle
    const numericPrice = price.replace(/[^\d]/g, '');
    if (numericPrice) {
      return `${numericPrice} TL`;
    }
  }
  
  // Eğer price number ise
  if (typeof price === 'number') {
    return `${price.toLocaleString('tr-TR')} TL`;
  }
  
  return price;
}

export default function IstanbulBox() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    district: "",
    deliveryYear: ""
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    async function fetchDistricts() {
      try {
        const q = query(collection(db, "projects"), where("city", "==", "İstanbul"));
        const querySnapshot = await getDocs(q);
        const uniqueDistricts = [...new Set(querySnapshot.docs.map(doc => doc.data().district))];
        setDistricts(uniqueDistricts.filter(Boolean).sort());
      } catch (error) {
        console.error("İlçeler alınırken hata oluştu:", error);
      }
    }

    fetchDistricts();
  }, []);

  useEffect(() => {
    async function fetchInitialProjects() {
      setLoading(true);
      try {
        const q = query(collection(db, "projects"), where("city", "==", "İstanbul"));
        const querySnapshot = await getDocs(q);
        const projectsList = [];
        querySnapshot.forEach((doc) => {
          projectsList.push({ id: doc.id, ...doc.data() });
        });
        setProjects(projectsList);
      } catch (error) {
        console.error("Projeler alınırken hata oluştu:", error);
      }
      setLoading(false);
    }

    if (!filtersApplied) {
      fetchInitialProjects();
    }
  }, [filtersApplied]);

  const applyFilters = async () => {
    setLoading(true);
    setFiltersApplied(true);
    try {
      let constraints = [where("city", "==", "İstanbul")];

      if (filters.district) {
        constraints.push(where("district", "==", filters.district));
      }
      if (filters.deliveryYear) {
        constraints.push(where("delivery_date", "==", filters.deliveryYear));
      }

      const q = query(collection(db, "projects"), ...constraints);
      const querySnapshot = await getDocs(q);
      const projectsList = [];
      querySnapshot.forEach((doc) => {
        projectsList.push({ id: doc.id, ...doc.data() });
      });
      setProjects(projectsList);
    } catch (error) {
      console.error("Projeler alınırken hata oluştu:", error);
    }
    setLoading(false);
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      district: "",
      deliveryYear: ""
    });
    setFiltersApplied(false);
    if (isMobile) {
      setShowFilters(false);
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div style={{...styles.container, ...(isMobile && styles.mobileContainer)}}>
      <h2 style={styles.header}>İstanbul Projeleri</h2>

      {/* Mobile Filter Toggle Button */}
      {isMobile && (
        <div style={styles.mobileFilterHeader}>
          <button style={styles.mobileFilterToggle} onClick={toggleFilters}>
            <svg style={styles.filterIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 21V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 10V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 21V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 21V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 12V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 14H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 16H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Filtrele
            {showFilters ? (
              <svg style={styles.chevronIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg style={styles.chevronIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      )}

      <div style={{...styles.filterSection, ...(isMobile && styles.mobileFilterSection)}}>
        {/* Filters Sidebar */}
        {(showFilters || !isMobile) && (
          <div style={{
            ...styles.filterSidebar, 
            ...(isMobile && styles.mobileSidebar),
            ...(isMobile && showFilters && styles.mobileSidebarOpen)
          }}>
            <div style={styles.filterGroup}>
              <h3 style={styles.filterTitle}>Filtrele</h3>
              <div style={styles.filterItem}>
                <label htmlFor="district" style={styles.filterLabel}>İlçe</label>
                <select
                  id="district"
                  name="district"
                  value={filters.district}
                  onChange={handleFilterChange}
                  style={{...styles.filterSelect, ...(isMobile && styles.mobileSelect)}}
                >
                  <option value="">Tüm İlçeler</option>
                  {districts.map((dist, i) => (
                    <option key={i} value={dist}>{dist}</option>
                  ))}
                </select>
              </div>

              <div style={styles.filterItem}>
                <label htmlFor="deliveryYear" style={styles.filterLabel}>Teslim Yılı</label>
                <select
                  id="deliveryYear"
                  name="deliveryYear"
                  value={filters.deliveryYear}
                  onChange={handleFilterChange}
                  style={{...styles.filterSelect, ...(isMobile && styles.mobileSelect)}}
                >
                  <option value="">Tüm Yıllar</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>
              </div>

              <div style={styles.filterButtons}>
                <button 
                  style={styles.applyButton}
                  onClick={applyFilters}
                >
                  Uygula
                </button>
                <button 
                  style={styles.resetFilters}
                  onClick={resetFilters}
                >
                  Temizle
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Overlay for mobile when filters are open */}
        {isMobile && showFilters && (
          <div style={styles.overlay} onClick={() => setShowFilters(false)} />
        )}

        <div style={styles.projectsContent}>
          {loading ? (
            <div style={styles.loading}>Projeler yükleniyor...</div>
          ) : projects.length === 0 ? (
            <div style={styles.noProjects}>
              {filtersApplied 
                ? "Filtrelerinize uygun proje bulunamadı." 
                : "İstanbul için proje bulunamadı."}
            </div>
          ) : (
            <div style={{...styles.grid, ...(isMobile && styles.mobileGrid)}}>
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    minHeight: "100vh",
  },
  mobileContainer: {
    padding: "15px",
  },
  header: {
    color: "#2a9d8f",
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "30px",
    textAlign: "center",
  },
  mobileFilterHeader: {
    width: "100%",
    marginBottom: "15px",
  },
  mobileFilterToggle: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "12px 16px",
    backgroundColor: "#2a9d8f",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  filterIcon: {
    width: "20px",
    height: "20px",
  },
  chevronIcon: {
    width: "18px",
    height: "18px",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 998,
  },
  filterSection: {
    display: "flex",
    gap: "30px",
  },
  mobileFilterSection: {
    flexDirection: "column",
  },
  filterSidebar: {
    width: "280px",
    backgroundColor: "#f8f9fa",
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    height: "fit-content",
    position: "sticky",
    top: "20px",
  },
  mobileSidebar: {
    width: "80%",
    maxWidth: "320px",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 999,
    maxHeight: "80vh",
    overflowY: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },
  mobileSidebarOpen: {
    animation: "slideIn 0.3s ease-out",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  filterTitle: {
    color: "#2a9d8f",
    fontSize: "20px",
    marginBottom: "10px",
    paddingBottom: "10px",
    borderBottom: "1px solid #e0e0e0",
  },
  filterItem: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  filterLabel: {
    fontSize: "16px",
    color: "#555",
    fontWeight: "500",
  },
  filterSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    backgroundColor: "#fff",
  },
  mobileSelect: {
    fontSize: "13px",
    padding: "6px 10px",
  },
  filterButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
  applyButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#2a9d8f",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
  },
  resetFilters: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#f8f9fa",
    color: "#555",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
  },
  projectsContent: {
    flex: 1,
    minHeight: "400px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "25px",
  },
  mobileGrid: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  card: {
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
  image: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderBottom: "1px solid #eee",
  },
  content: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  title: {
    fontSize: "20px",
    color: "#333",
    margin: 0,
    lineHeight: "1.3",
  },
  locationContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "4px",
  },
  priceContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  icon: {
    width: "16px",
    height: "16px",
    fill: "#555",
    flexShrink: 0,
  },
  location: {
    fontSize: "14px",
    color: "#777",
    margin: 0,
    fontWeight: "500",
  },
  price: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#2a9d8f",
    margin: 0,
    textAlign: "left",
  },
  description: {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.4",
    marginTop: "8px",
  },
  loading: {
    padding: "50px",
    textAlign: "center",
    fontSize: "20px",
  },
  noProjects: {
    padding: "50px",
    textAlign: "center",
    fontSize: "20px",
    color: "#999",
  },
};

// CSS animation for mobile sidebar
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -45%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
`, styleSheet.cssRules.length);

// Mobil kartlar için özel stil
const mobileCardStyle = {
  width: "80%",
  margin: "0 auto",
};

// Mobil modda kartlara özel stil uygula
if (typeof window !== 'undefined' && window.innerWidth <= 768) {
  const style = document.createElement('style');
  style.textContent = `
    @media (max-width: 768px) {
      .project-card {
        width: 80% !important;
        margin: 0 auto !important;
      }
    }
  `;
  document.head.appendChild(style);
}